import { ThresholdDirection, ThresholdInterval } from '@toggle/chart';
import { formatTicker, SnakeMetaThreshold } from '@toggle/toggle';

import type { CreateChartDataItemProps, TerminalChartAssetData } from './types';

export const createThresholdIntervals = <
  T extends Array<{ close: number; time: string }>
>({
  ts,
  primaryAsset,
  threshold,
}: {
  ts: T;
  primaryAsset: TerminalChartAssetData;
  threshold: Required<SnakeMetaThreshold>;
}) => {
  const intervals: ThresholdInterval[] = [];
  const tsByTime = primaryAsset.tsByTime;
  const positiveCheck =
    threshold.good > threshold.bad
      ? (item: T[0]) => item.close >= threshold.good
      : (item: T[0]) => item.close <= threshold.good;
  const negativeCheck =
    threshold.good < threshold.bad
      ? (item: T[0]) => item.close >= threshold.bad
      : (item: T[0]) => item.close <= threshold.bad;
  const mapping: Record<'positive' | 'negative', number> = {
    positive: threshold.good,
    negative: threshold.bad,
  };
  let start: number | undefined = undefined;
  let direction: ThresholdDirection | undefined = undefined;

  const addInterval = (item: T[0], variant: 'positive' | 'negative') => {
    const startIdx = start as number;
    intervals.push({
      direction: variant,
      start: startIdx,
      end: tsByTime.get(item.time) as number,
      threshold: mapping[variant],
    });
    direction = undefined;
    start = undefined;
  };

  ts.forEach(item => {
    if (positiveCheck(item)) {
      if (direction === 'negative') {
        addInterval(item, direction);
      }

      direction = 'positive';
      start = start ?? tsByTime.get(item.time);
    } else if (negativeCheck(item)) {
      if (direction === 'positive') {
        addInterval(item, direction);
      }

      direction = 'negative';
      start = start ?? tsByTime.get(item.time);
    } else if (direction === 'positive' || direction === 'negative') {
      addInterval(item, direction);
    }
  });

  if (direction && start !== undefined) {
    intervals.push({
      direction,
      start: start,
      end: tsByTime.get(ts[ts.length - 1].time) ?? primaryAsset.ts.length - 1,
      threshold: mapping[direction as 'positive' | 'negative'],
    });
  }

  return intervals;
};

export const createChartDataItem = ({
  entity,
  ts,
  lineColorToken,
  primaryAsset,
  isHidden = false,
  snakeMeta,
  episodes,
}: CreateChartDataItemProps): TerminalChartAssetData => {
  let filteredTs =
    !primaryAsset || primaryAsset.entity.default_snake === entity.default_snake
      ? ts
      : ts.filter(t => primaryAsset.tsByTime.get(t.time) !== undefined);

  if (filteredTs.length === 0) {
    filteredTs = ts;
  }

  const tsByTime = new Map();
  filteredTs.forEach((item, idx) => tsByTime.set(item.time, idx));
  const thresholdIntervals =
    primaryAsset && snakeMeta?.threshold
      ? createThresholdIntervals({
        primaryAsset,
        ts: filteredTs,
        threshold: snakeMeta.threshold as Required<SnakeMetaThreshold>,
      })
      : undefined;

  return {
    entity: {
      ...entity,
      formattedTicker: formatTicker(entity),
      logo: '', // Todo:  pass logo as prop
    },
    ts: filteredTs,
    originalTs: ts,
    lineColorToken,
    tsByTime,
    isHidden,
    snakeMeta,
    thresholdIntervals,
    episodesData: episodes,
  };
};
